// Lux Tonnetz grid and its Harmonic Luminosity score. Pure functions, no React:
// components/LuxTonnetz.tsx draws what buildTonnetz returns.
//
// Ported from the horiz_vert_tonnetz_hexs prototype, whose README documents the
// lighting rules, the horizon and the score's measured results in full.

// Line-of-fifths position of each natural (C = 0, G = 1, F = -1, ...)
const NATURAL_FIFTHS: Record<string, number> = { F: -1, C: 0, G: 1, D: 2, A: 3, E: 4, B: 5 };
const FIFTHS_LETTERS = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];

// Modulo that stays positive for negative inputs
export const mod = (n: number, m: number): number => ((n % m) + m) % m;

// Pitch class (0-11) of a line-of-fifths position
export const pitchClassOf = (fifths: number): number => mod(fifths * 7, 12);

// Line-of-fifths position -> spelled note name, e.g. 6 -> 'F♯', -14 -> 'C♭♭'
export const fifthsToNote = (fifths: number): string => {
  const letter = FIFTHS_LETTERS[mod(fifths + 1, 7)];
  const accidentals = Math.floor((fifths + 1) / 7);
  return letter + (accidentals >= 0 ? '♯'.repeat(accidentals) : '♭'.repeat(-accidentals));
};

// Note name -> line-of-fifths position. Accepts 'B♭' or 'Bb', 'C♯' or 'C#'.
export const noteToFifths = (name: string): number | null => {
  const match = /^([A-Ga-g])([♯#♭b]*)$/.exec(name.trim());
  if (!match) return null;
  const letter = match[1];
  const accidentals = match[2];
  const sharps = (accidentals.match(/[♯#]/g) || []).length;
  const flats = (accidentals.match(/[♭b]/g) || []).length;
  return NATURAL_FIFTHS[letter.toUpperCase()] + 7 * (sharps - flats);
};

// Compares rank arrays entry by entry; negative means the first one wins
const compareRanks = (a: number[], b: number[]): number => {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
};

// Harmonic Luminosity Scale, ported from luminosity1.7.html.
//
// Each note is checked for the thirds built on it. A major third makes it bright, a minor
// third dark, and a third plus a fifth — a full triad — counts more than a bare third.
// An augmented triad is bright thirds stacked, so it adds darkness to cancel them: the
// doc's point that listeners hear augmented chords as low-valenced. Diminished chords need
// no special case, being stacked minor thirds the minor weights already darken.
//
// The weights are each degree's distance around the circle of fifths from the darkest
// chord of its kind: bright is measured from ♭VI (0) out to II (6), dark from VI (0) out
// to ♭III (6). Keys are indexed by scale degree, so this is already key-relative.
const LUMINOSITY_BRIGHT_WEIGHT: Record<number, number> = { 2: 6, 7: 5, 0: 4, 5: 3, 10: 2, 3: 1, 8: 0, 1: 1, 6: 2, 11: 3, 4: 4, 9: 5 };
const LUMINOSITY_DARK_WEIGHT: Record<number, number> = { 3: 6, 8: 5, 1: 4, 6: 3, 11: 2, 4: 1, 9: 0, 2: 1, 7: 2, 0: 3, 5: 4, 10: 5 };
// A bare third counts for less than a full triad
const MAJOR_DYAD_REDUCTION = 1.5;
const MINOR_DYAD_REDUCTION = 2.0;
// Thirds are the whole of the published scale, which leaves chords built without any — C7sus,
// quartal voicings — scoring a flat zero. So each note in the grey row also contributes a
// little for how far it sits along the line of fifths: sharp of the key center is bright,
// flat is dark. Small enough not to disturb the order the thirds produce.
const GREY_POSITION_WEIGHT = 0.1;
// The chord on the key center counted a second time: major tonic bright, minor dark, and a
// tonic whose fifth is diminished or augmented darker still. A bare third with no fifth
// under it counts half.
const TONIC_CHORD_WEIGHT = 2;
const TONIC_ALTERED_FIFTH = 1.5;

// Contrast is the harmonic mean of the bright half and the dark half's size. It is 0 when
// either half is 0 and sits near the smaller half when they are lopsided, so only a set
// with real weight on both sides scores high: a major triad, almost all bright, scores
// barely above 0. Checked rather than caught: 0 / 0 is NaN in JavaScript, not an error.
// `dark` is never positive, and bright never negative, so their sizes never cancel.
const contrastOf = (bright: number, dark: number): number => {
  const darkness = Math.abs(dark);
  const total = bright + darkness;
  return total === 0 ? 0 : (2 * bright * darkness) / total;
};

export interface Luminosity {
  bright: number;
  dark: number;
  net: number;
  contrast: number;
}

export interface TonnetzInput {
  numColumns: number;
  startValue: number;
  /** Key center's line-of-fifths position. */
  shift: number;
  /** Column of the key center in row 2. */
  midway: number;
  /** Semitones above the key center to light. */
  activeScaleDegrees: number[];
}

export interface Tonnetz {
  noteMatrix: string[][];
  /** Each cell's line-of-fifths position. */
  distances: number[][];
  activeMatrix: boolean[][];
  /** Raw totals, before density dampening — see dampenLuminosity. */
  luminosity: Luminosity;
}

// Note names and lit cells for the 5-row grid. Row 2 holds the key center at column `midway`.
export const buildTonnetz = ({ numColumns, startValue, shift, midway, activeScaleDegrees }: TonnetzInput): Tonnetz => {
  // Each column step is +1 fifth. Relative to a cell, up-right is +4 (major third up)
  // and up-left is +3 (minor third down); two rows up is +7 (same letter, sharpened).
  const rowOffsets = [0, -4, -7, -11, -14];
  const distances = rowOffsets.map(offset =>
    Array.from({ length: numColumns }, (_, i) => startValue + offset + i + shift)
  );
  const noteMatrix = distances.map(row => row.map(fifthsToNote));

  const keyPitchClass = pitchClassOf(shift);
  const activePitchClasses = new Set(activeScaleDegrees.map(d => mod(d + keyPitchClass, 12)));
  const pitchClassMatches = (row: number, col: number) => activePitchClasses.has(pitchClassOf(distances[row][col]));

  // Lights the grid from a chosen set of lit row-2 columns.
  // Connections only run diagonally to the right, away from row 2: a cell in rows 1/3 lights
  // only if it's the up-right/down-right of a lit row-2 note, and rows 0/4 likewise from rows 1/3.
  const lightGrid = (row2Cols: number[]): boolean[][] => {
    const active = distances.map(row => row.map(() => false));
    row2Cols.forEach(col => { active[2][col] = true; });
    for (const row of [1, 3]) {
      for (let col = 1; col < numColumns; col++) {
        active[row][col] = pitchClassMatches(row, col) && active[2][col - 1];
      }
    }
    for (let col = 0; col < numColumns; col++) {
      active[0][col] = pitchClassMatches(0, col) && active[1][col];
      active[4][col] = pitchClassMatches(4, col) && active[3][col];
    }
    return active;
  };

  // Row 2 lights each active pitch class once. Walk outward from the key center
  // (root, 1 right, 1 left, 2 right, 2 left, ...). The first time a pitch class is reached,
  // pick between its row-2 copies in this order:
  //   1. the copy closest to an already-lit row-2 cell, so chords stay together
  //   2. the copy just reached, which is the closest to the key center (right-hand one on a tie)
  const visitOrder = [midway];
  for (let step = 1; step <= Math.max(midway, numColumns - 1 - midway); step++) {
    visitOrder.push(midway + step, midway - step);
  }

  const litRow2Cols: number[] = [];
  const decidedPitchClasses = new Set<number>();
  const distanceToLitCell = (col: number) =>
    Math.min(Infinity, ...litRow2Cols.map(lit => Math.abs(lit - col)));

  for (const col of visitOrder) {
    if (col < 0 || col >= numColumns || !pitchClassMatches(2, col)) continue;
    const pc = pitchClassOf(distances[2][col]);
    if (decidedPitchClasses.has(pc)) continue;
    decidedPitchClasses.add(pc);

    // Lower ranks win, compared entry by entry
    const rank = (candidate: number) => [distanceToLitCell(candidate), candidate === col ? 0 : 1];
    let chosen = col;
    for (let other = 0; other < numColumns; other++) {
      if (other === col || pitchClassOf(distances[2][other]) !== pc) continue;
      if (compareRanks(rank(other), rank(chosen)) < 0) chosen = other;
    }
    litRow2Cols.push(chosen);
  }

  const activeMatrix = lightGrid(litRow2Cols);

  // Luminosity, read off the lit cells rather than the pitch-class set, so the number only
  // ever counts what the grid is showing. Verified to match the pitch-class version of the
  // same algorithm on the diatonic modes, harmonic minor/major, and assorted triads.
  // For each lit grey cell, the cells one column right hold its thirds: yellow in row 1 is
  // its major third, blue in row 3 its minor third, grey in row 2 its fifth, orange in
  // row 0 the augmented fifth above its major third.
  let bright = 0;
  let dark = 0;
  for (let col = 0; col < numColumns; col++) {
    if (!activeMatrix[2][col]) continue;

    // What the note's own position is worth, thirds aside
    const fifthsFromKey = distances[2][col] - shift;
    if (fifthsFromKey > 0) bright += fifthsFromKey * GREY_POSITION_WEIGHT;
    else dark += fifthsFromKey * GREY_POSITION_WEIGHT;

    if (col + 1 >= numColumns) continue;
    const degree = pitchClassOf(distances[2][col] - shift);
    const hasMajorThird = activeMatrix[1][col + 1];
    const hasMinorThird = activeMatrix[3][col + 1];
    const hasFifth = activeMatrix[2][col + 1];
    const hasDiminishedFifth = activeMatrix[4][col + 1];
    const hasAugmentedFifth = activeMatrix[0][col + 1];

    if (hasMajorThird && hasFifth) bright += LUMINOSITY_BRIGHT_WEIGHT[degree];
    else if (hasMajorThird) bright += LUMINOSITY_BRIGHT_WEIGHT[degree] / MAJOR_DYAD_REDUCTION;

    if (hasMinorThird && hasFifth) dark -= LUMINOSITY_DARK_WEIGHT[degree];
    else if (hasMinorThird) dark -= LUMINOSITY_DARK_WEIGHT[degree] / MINOR_DYAD_REDUCTION;

    if (hasMinorThird && hasDiminishedFifth) {
      // Weights the diminished triad as dark as a minor chord built on its own third
      dark -= LUMINOSITY_DARK_WEIGHT[mod(degree + 3, 12)] * 0.60;
    }

    // An augmented triad is almost as dark (80%) as the minor triad a fourth above it: C+ costs what Fm
    // does. C–E–G♯ stands as C+, E+ and G♯+ at once, so it is charged in all three places —
    // that is what keeps an augmented chord's three bright major thirds from carrying it.
    if (hasMajorThird && hasAugmentedFifth) {
      dark -= LUMINOSITY_DARK_WEIGHT[mod(degree + 5, 12)] * 0.80;
    }
  }

  // The chord standing on the key center is heard as home, so it scores once more on top of
  // its ordinary turn above. Without this the score cannot tell two transpositions of one
  // scale apart — half-whole diminished, whose tonic chord is major, comes out the same as
  // whole-half, whose tonic is diminished. Nothing lights here when the key center is unlit,
  // so a scale that leaves out its own root scores no tonic term at all.
  const tonicMajorThird = activeMatrix[1][midway + 1];
  const tonicMinorThird = activeMatrix[3][midway + 1];
  const tonicFifth = activeMatrix[2][midway + 1];
  const tonicAugmentedFifth = activeMatrix[0][midway + 1];
  const tonicDiminishedFifth = activeMatrix[4][midway + 1];
  if (tonicMajorThird && tonicFifth) bright += TONIC_CHORD_WEIGHT;
  else if (tonicMinorThird && tonicFifth) dark -= TONIC_CHORD_WEIGHT;
  else if (tonicMinorThird && tonicDiminishedFifth) dark -= TONIC_CHORD_WEIGHT * TONIC_ALTERED_FIFTH;
  else if (tonicMajorThird && tonicAugmentedFifth) dark -= TONIC_CHORD_WEIGHT * TONIC_ALTERED_FIFTH;
  else if (tonicMajorThird) bright += TONIC_CHORD_WEIGHT / 2;
  else if (tonicMinorThird) dark -= TONIC_CHORD_WEIGHT / 2;

  const luminosity = { bright, dark, net: bright + dark, contrast: contrastOf(bright, dark) };

  return { noteMatrix, distances, activeMatrix, luminosity };
};

// Density dampening: both halves divided by noteCount ^ 0.33, so a seven-note scale is not
// louder than a triad merely for having more notes. Kept apart from buildTonnetz, which
// returns the raw totals.
export const dampenLuminosity = (raw: Luminosity, noteCount: number): Luminosity => {
  const dampen = Math.pow(Math.max(1, noteCount), 0.33);
  const bright = raw.bright / dampen;
  const dark = raw.dark / dampen;
  return { bright, dark, net: bright + dark, contrast: contrastOf(bright, dark) };
};
